import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import { useCanMutate } from '../../../hooks/useCanMutate';
import produccionGuiaService from '../../../services/produccionGuiaService';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { getGuiaConfigBySlug } from '../../../utils/produccionGuiaPaths';
import {
  downloadProduccionGuiaTemplate,
  parseProduccionGuiaQuantitiesImport,
} from '../../../utils/produccionGuiaExcelImport';

const MAX_INT_DIGITS = 4;
const MAX_DEC_DIGITS = 4;
const QTY_FACTOR = 10 ** MAX_DEC_DIGITS;

function sanitizeQty(value) {
  if (value === '') return '';
  const s = String(value).replace(/[^\d.]/g, '');
  const dotIdx = s.indexOf('.');
  let intPart = dotIdx === -1 ? s : s.slice(0, dotIdx);
  if (intPart.length > MAX_INT_DIGITS) intPart = intPart.slice(0, MAX_INT_DIGITS);
  if (dotIdx === -1) return intPart;
  let decPart = s.slice(dotIdx + 1).replace(/\./g, '');
  if (decPart.length > MAX_DEC_DIGITS) decPart = decPart.slice(0, MAX_DEC_DIGITS);
  return intPart + '.' + decPart;
}

const GuiasProduccionForm = () => {
  const { tipoSlug, id: editId } = useParams();
  const navigate = useNavigate();
  const config = getGuiaConfigBySlug(tipoSlug);
  const { produccionLineasEnabled } = useModuleSettings();
  const isEdit = Boolean(editId);
  const canMutate = useCanMutate();
  const readOnly = !canMutate;

  const basePath = `/produccion/guias/${tipoSlug}`;

  const [guideDate, setGuideDate] = useState('');
  const [guideTime, setGuideTime] = useState('08:00');
  const [warehouseId, setWarehouseId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [lineFilter, setLineFilter] = useState('all');
  const [notes, setNotes] = useState('');
  const [code, setCode] = useState('');
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const excelInputRef = useRef(null);

  const mapRows = (list) =>
    (list || []).map((r) => ({
      receta_plato_id: r.receta_plato_id,
      receta_name: r.receta_name,
      receta_code: r.receta_code,
      classification: r.classification,
      output_quantity: r.output_quantity,
      output_unit_of_measure: r.output_unit_of_measure,
      quantity: r.quantity != null && r.quantity !== '' ? String(r.quantity) : '',
    }));

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const optRes = await produccionGuiaService.formOptions();
      if (optRes.success && optRes.data) {
        setWarehouses(optRes.data.warehouses || []);
        setProjects(optRes.data.projects || []);
        setLineas(optRes.data.lineas || []);
      }

      if (isEdit) {
        const res = await produccionGuiaService.get(editId);
        if (!res.success || !res.data) {
          setToast({ isOpen: true, message: res.message || 'Guía no encontrada', type: 'error' });
          return;
        }
        const d = res.data;
        setCode(d.code || '');
        setGuideDate(d.guide_date || '');
        setGuideTime(d.guide_time || '08:00');
        setWarehouseId(d.warehouse_id != null ? String(d.warehouse_id) : '');
        setProjectId(d.project_id != null ? String(d.project_id) : '');
        setNotes(d.notes || '');
        if (produccionLineasEnabled) {
          setLineFilter(d.produccion_linea_id != null ? String(d.produccion_linea_id) : 'all');
        }
        setRows(mapRows(d.rows).map((r) => ({ ...r, quantity: r.quantity || '' })));
      } else {
        const templateParams = {};
        if (produccionLineasEnabled && lineFilter !== 'all') {
          templateParams.produccion_linea_id = lineFilter;
        }
        const res = await produccionGuiaService.template(config.guideType, templateParams);
        if (!res.success || !res.data) {
          setToast({ isOpen: true, message: res.message || 'No se pudo cargar plantilla', type: 'error' });
          return;
        }
        const d = res.data;
        setGuideDate(d.guide_date || '');
        setGuideTime(d.guide_time || '08:00');
        setNotes(d.notes || '');
        setRows(mapRows(d.rows).map((r) => ({ ...r, quantity: '' })));
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [config, isEdit, editId, produccionLineasEnabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!config || isEdit || !produccionLineasEnabled) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const templateParams = {};
        if (lineFilter !== 'all') templateParams.produccion_linea_id = lineFilter;
        const res = await produccionGuiaService.template(config.guideType, templateParams);
        if (cancelled || !res.success || !res.data) return;
        setRows(mapRows(res.data.rows).map((r) => ({ ...r, quantity: '' })));
      } catch {
        if (!cancelled) {
          setToast({ isOpen: true, message: 'Error al filtrar recetas por línea', type: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lineFilter, config, isEdit, produccionLineasEnabled]);

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.receta_name && r.receta_name.toLowerCase().includes(q)) ||
        (r.receta_code && String(r.receta_code).toLowerCase().includes(q)) ||
        (r.classification && r.classification.toLowerCase().includes(q)),
    );
  }, [rows, filterText]);

  const filledCount = useMemo(
    () => rows.filter((r) => r.quantity !== '' && parseFloat(String(r.quantity).replace(',', '.')) > 0).length,
    [rows],
  );

  const updateRowQty = (recetaId, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.receta_plato_id === recetaId ? { ...r, quantity: sanitizeQty(value) } : r,
      ),
    );
  };

  const proyectosList = useMemo(
    () => projects.filter((p) => p.type === 'proyecto'),
    [projects],
  );
  const operacionesList = useMemo(
    () => projects.filter((p) => p.type === 'operacion'),
    [projects],
  );

  const handleDownloadTemplate = () => {
    downloadProduccionGuiaTemplate({
      rows,
      guideTypeLabel: config.shortTitle,
    });
  };

  const handleExcelPick = () => {
    if (excelInputRef.current) {
      excelInputRef.current.value = '';
      excelInputRef.current.click();
    }
  };

  const handleExcelChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setImportingExcel(true);
    try {
      const result = await parseProduccionGuiaQuantitiesImport(file, rows);
      if (!result.success) {
        setToast({ isOpen: true, message: result.fatalMessage || 'No se pudo leer el archivo', type: 'error' });
        return;
      }
      setRows(result.rows);
      let msg = `Se cargaron ${result.applied} cantidad(es) desde el Excel.`;
      if (result.errors?.length) {
        msg += ` ${result.errors.length} fila(s) con aviso (ver consola).`;
        console.warn('[Guía producción import]', result.errors);
      }
      setToast({ isOpen: true, message: msg, type: result.errors?.length ? 'info' : 'success' });
    } catch (err) {
      setToast({ isOpen: true, message: err?.message || 'Error al importar Excel', type: 'error' });
    } finally {
      setImportingExcel(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!guideDate) {
      setToast({ isOpen: true, message: 'Indique la fecha de la guía', type: 'error' });
      return;
    }
    if (!guideTime) {
      setToast({ isOpen: true, message: 'Indique la hora de la guía', type: 'error' });
      return;
    }
    if (!warehouseId) {
      setToast({ isOpen: true, message: 'Seleccione el almacén de salida', type: 'error' });
      return;
    }
    if (!projectId) {
      setToast({ isOpen: true, message: 'Seleccione el proyecto u operación', type: 'error' });
      return;
    }

    const items = [];
    for (const r of rows) {
      if (r.quantity === '') continue;
      const qRaw = parseFloat(String(r.quantity).replace(',', '.'));
      if (!Number.isFinite(qRaw) || qRaw <= 0) continue;
      const q = Math.round(qRaw * QTY_FACTOR) / QTY_FACTOR;
      items.push({ receta_plato_id: r.receta_plato_id, quantity: q });
    }

    if (items.length === 0) {
      setToast({
        isOpen: true,
        message: 'Indique al menos una cantidad elaborada mayor a cero',
        type: 'error',
      });
      return;
    }

    const payload = {
      guide_type: config.guideType,
      guide_date: guideDate,
      guide_time: guideTime,
      warehouse_id: Number(warehouseId),
      project_id: Number(projectId),
      notes: notes.trim() || null,
      items,
    };

    if (produccionLineasEnabled && lineFilter !== 'all') {
      payload.produccion_linea_id = Number(lineFilter);
    } else if (produccionLineasEnabled) {
      payload.produccion_linea_id = null;
    }

    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await produccionGuiaService.update(editId, {
          guide_date: payload.guide_date,
          guide_time: payload.guide_time,
          warehouse_id: payload.warehouse_id,
          project_id: payload.project_id,
          produccion_linea_id: payload.produccion_linea_id,
          notes: payload.notes,
          items: payload.items,
        });
      } else {
        res = await produccionGuiaService.create(payload);
      }
      if (res.success) {
        const savedId = res.data?.id ?? (isEdit ? editId : null);
        setToast({
          isOpen: true,
          message: isEdit ? 'Guía actualizada' : 'Guía guardada',
          type: 'success',
        });
        if (savedId && items.length > 0) {
          setTimeout(() => navigate(`${basePath}/analisis/${savedId}`), 600);
        } else {
          setTimeout(() => navigate(basePath), 600);
        }
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo guardar', type: 'error' });
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.data?.errors && Object.values(err.response.data.errors).flat().join(' ')) ||
        err.message;
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-red-600">Tipo de guía no válido.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex justify-center text-gray-600">Cargando guía…</div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/produccion" className="hover:text-teal-600">
          Producción
        </Link>
        <span className="mx-2">/</span>
        <Link to={basePath} className="hover:text-teal-600">
          {config.shortTitle}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">{isEdit ? 'Editar' : 'Nueva'}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEdit ? `Editar ${config.title.toLowerCase()}` : config.title}
        </h1>
        {isEdit && filledCount > 0 ? (
          <Link
            to={`${basePath}/analisis/${editId}`}
            className="shrink-0 text-sm font-medium text-teal-800 border border-teal-200 bg-teal-50 rounded-md px-3 py-2 hover:bg-teal-100"
          >
            Ver costos e insumos
          </Link>
        ) : null}
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Todas las recetas de <strong>{config.shortTitle.toLowerCase()}</strong> aparecen abajo; solo complete las
        cantidades elaboradas.
        {filledCount > 0 ? (
          <span className="text-teal-700 font-medium"> ({filledCount} con cantidad)</span>
        ) : null}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {isEdit ? (
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-mono">{code}</div>
              </div>
            ) : null}
            <div>
              <label htmlFor="guia-fecha" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha <span className="text-red-500">*</span>
              </label>
              <input
                id="guia-fecha"
                type="date"
                value={guideDate}
                onChange={(e) => setGuideDate(e.target.value)}
                disabled={readOnly}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label htmlFor="guia-hora" className="block text-sm font-medium text-gray-700 mb-1">
                Hora <span className="text-red-500">*</span>
              </label>
              <input
                id="guia-hora"
                type="time"
                value={guideTime}
                onChange={(e) => setGuideTime(e.target.value)}
                disabled={readOnly}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label htmlFor="guia-almacen" className="block text-sm font-medium text-gray-700 mb-1">
                Almacén de salida <span className="text-red-500">*</span>
              </label>
              <select
                id="guia-almacen"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                disabled={readOnly}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">Seleccione almacén…</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.code ? `${w.code} — ` : ''}
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="guia-proyecto" className="block text-sm font-medium text-gray-700 mb-1">
                Proyecto u operación <span className="text-red-500">*</span>
              </label>
              <select
                id="guia-proyecto"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={readOnly}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">Seleccione…</option>
                {proyectosList.length > 0 ? (
                  <optgroup label="Proyectos">
                    {proyectosList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code ? `${p.code} — ` : ''}
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {operacionesList.length > 0 ? (
                  <optgroup label="Operaciones">
                    {operacionesList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code ? `${p.code} — ` : ''}
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label htmlFor="guia-notas" className="block text-sm font-medium text-gray-700 mb-1">
                Observaciones
              </label>
              <input
                id="guia-notas"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={readOnly}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                placeholder="Opcional"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recetas</h2>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center">
              {produccionLineasEnabled && (
                <select
                  value={lineFilter}
                  onChange={(e) => setLineFilter(e.target.value)}
                  disabled={readOnly}
                  className="w-full sm:w-48 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                >
                  <option value="all">Todas las líneas</option>
                  {lineas.map((linea) => (
                    <option key={linea.id} value={linea.id}>
                      {linea.name}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="search"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filtrar por nombre o clasificación…"
                className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500"
              />
              {canMutate ? (
                <>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50"
                  >
                    Descargar plantilla
                  </button>
                  <button
                    type="button"
                    onClick={handleExcelPick}
                    disabled={importingExcel || rows.length === 0}
                    className="px-3 py-2 border border-teal-200 bg-teal-50 rounded-md text-sm font-medium text-teal-900 hover:bg-teal-100 disabled:opacity-50"
                  >
                    {importingExcel ? 'Cargando…' : 'Cargar cantidades (Excel)'}
                  </button>
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    className="hidden"
                    onChange={handleExcelChange}
                  />
                </>
              ) : null}
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              No hay recetas activas de tipo «{config.shortTitle}». Cree recetas en Configuración antes de registrar la
              guía.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 max-h-[32rem] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium min-w-[10rem]">Receta</th>
                    <th className="text-left px-3 py-2 font-medium">Clasificación</th>
                    <th className="text-left px-3 py-2 font-medium whitespace-nowrap w-28">Salida estándar</th>
                    <th className="text-left px-3 py-2 font-medium w-36">
                      <span className="block">
                        Cantidad elaborada <span className="text-red-500">*</span>
                      </span>
                      <span className="block text-[0.7rem] font-normal text-gray-500 normal-case">
                        (misma unidad que salida estándar)
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row) => (
                    <tr key={row.receta_plato_id} className="hover:bg-gray-50/80">
                      <td className="px-3 py-2 align-middle">
                        <div className="font-medium text-gray-900">{row.receta_name}</div>
                        {row.receta_code ? (
                          <div className="text-xs text-gray-500 font-mono">{row.receta_code}</div>
                        ) : null}
                        {row.multi_output_enabled || Number(row.outputs_count) > 1 ? (
                          <div className="text-[0.65rem] mt-0.5 text-teal-700 font-medium">
                            Multi-salida ({row.outputs_count || '2+'} productos)
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-middle text-gray-600 text-xs leading-snug">
                        {row.classification}
                      </td>
                      <td className="px-3 py-2 align-middle whitespace-nowrap text-gray-700 font-mono text-xs">
                        {row.output_quantity} {row.output_unit_of_measure}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={row.quantity}
                          onChange={(e) => updateRowQty(row.receta_plato_id, e.target.value)}
                          disabled={readOnly}
                          className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono tabular-nums focus:ring-1 focus:ring-teal-500 disabled:bg-gray-100"
                          placeholder="0"
                          autoComplete="off"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filterText && filteredRows.length === 0 && rows.length > 0 ? (
            <p className="text-sm text-gray-500 mt-2">Ninguna receta coincide con el filtro.</p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3">
          {canMutate ? (
            <button
              type="submit"
              disabled={saving || rows.length === 0}
              className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Guardar guía'}
            </button>
          ) : null}
          <Link
            to={basePath}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 inline-flex items-center"
          >
            {readOnly ? 'Volver' : 'Cancelar'}
          </Link>
        </div>
      </form>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default GuiasProduccionForm;
